// src/utils/crashReporter.js
// Firebase-native error tracking for web.
// Logs unhandled errors to a `crashReports` Firestore collection
// so you can review them in the Firebase Console.
//
// Crashlytics only supports iOS/Android/Unity — this is the web equivalent.

import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';

/**
 * Report a caught error to Firestore for later review.
 * Safe to call from any context — silently fails if Firestore is unreachable.
 */
export async function reportCrash(error, context = {}) {
  try {
    const user = auth.currentUser;

    await addDoc(collection(db, 'crashReports'), {
      message: error?.message || String(error),
      stack: error?.stack || null,
      componentStack: context.componentStack || null,
      url: window.location.href,
      userAgent: navigator.userAgent,
      userId: user?.uid || null,
      userEmail: user?.email || null,
      context: context.extra || null,
      environment: process.env.NODE_ENV,
      appVersion: process.env.REACT_APP_VERSION || '1.0.0',
      timestamp: serverTimestamp(),
    });
  } catch {
    // Silently fail — we don't want error reporting to cause more errors
  }
}

/**
 * Set up global unhandled error + promise rejection listeners.
 * Call once at app startup (e.g., in index.js).
 */
export function initCrashReporting() {
  // Catch unhandled JS errors
  window.addEventListener('error', (event) => {
    reportCrash(event.error || new Error(event.message), {
      extra: { type: 'window.onerror', filename: event.filename, lineno: event.lineno },
    });
  });

  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    reportCrash(error, { extra: { type: 'unhandledrejection' } });
  });
}
